import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';
import { useLanguage } from '../../Context/LanguageContext';
import FetchModule from '../../Model/Share/Network/FetchModule';
import { formatPhoneNumber } from '../../Utils/phoneUtils';

import LoadingAnimation from '../../Animation/NewLoading.json';

import { ReactComponent as SearchingSvg } from '../../image/Searching.svg';
import { ReactComponent as Check2Svg } from '../../image/Check2.svg';
import { ReactComponent as DefaultLogoSvg } from '../../image/DefaultLogo.svg';

const LockerMemberSearchInput = forwardRef((props, ref) => {
	const { className, placeholder, type, disabled, onClickMembership } = props;

  const history = useHistory();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t, languageMode } = useLanguage();

	const [value, setValue] = useState('');
  const [dataList, setDataList] = useState([]);
  const [data, setData] = useState([]);
  const [index, setIndex] = useState(-1);
  const [selectedMember, setSelectedMember] = useState(null);
	const [changed, setChanged] = useState(false);
	const [changedPlaceholderColor, setChangedPlaceholderColor] = useState(false);
  const [isHoverMembership, setIsHoverMembership] = useState(false);
	const [clickCount, setClickCount] = useState(0);
	const [defaultPlaceholder, setDefaultPlaceholder] = useState(placeholder);
  const [isLoading, setIsLoading] = useState(true);

	const inputRef = useRef();
  const InfoWrapperRef = useRef();

	useImperativeHandle(ref, () => ({
		setValue: (val) => {
			setValue(val);
			setChanged(true);
		},
		getValue: () => {
      if (selectedMember) return selectedMember;
      if (index === -1) return 'None';
      return dataList[index];
		},
    setPrefilledMember: (memberData) => {
      setValue(memberData.name);
      setChanged(true);
      setSelectedMember(memberData);
    },
    getDataList: () => {
      return dataList;
    },
		plusClickCount: () => {
			setClickCount(clickCount + 1)
		},
		getClickCount: () => {
			return clickCount;
		},
		setDefaultPlaceholder: (val) => {
			setDefaultPlaceholder(val)
			setChangedPlaceholderColor(true);
			inputRef.current.style.borderColor = '#FF3131';
		},
		reset: (val) => {
			setClickCount(0);
			setDefaultPlaceholder(val)
			inputRef.current.style.borderColor = theme.line;
		}
	}));

  // disabled 상태에서 선택된 회원 전체 데이터 로드 및 자동 스크롤 //
  useEffect(() => {
    if (!disabled || !selectedMember || dataList.length === 0) return;

    if (selectedMember.isChecked === undefined) {
      const fullMemberData = dataList.find(m => m.memberId === selectedMember.memberId);
      if (fullMemberData) {
        setSelectedMember({ ...fullMemberData, isChecked: true });
        return;
      }
    }

    if (!InfoWrapperRef.current) return;
    const target = InfoWrapperRef.current.querySelector(`[data-member-id="${selectedMember.memberId}"]`);
    if (target) target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [disabled, selectedMember, dataList])

  // 라커 사용 가능 회원 목록 불러오기 //
  useEffect(() => {
    setIsLoading(true);
    const fetchModule = new FetchModule();
		(
			async () => {
				const responseData = await fetchModule.getDataVersion2('locker/available/member/', 'GET');

        if (responseData.status === 200) {
          const tmpList = responseData.data.slice();

          for (let i = 0; i < tmpList.length; i++) {
            tmpList[i] = Object.assign(tmpList[i], { isChecked: false })
          }
          setData(tmpList);
          setDataList(tmpList);
        }
        else if (responseData.status === 401) history.push('/login');
        setIsLoading(false);
			}
		)();
  }, [history])

	function onClickItem(index) {
    if (disabled) return;
    const tmpList = dataList.slice();

		for (let i = 0; i < dataList.length; i++) {
      tmpList[i].isChecked = false;
    }

    tmpList[index].isChecked = true;

    setDataList(tmpList);
    setIndex(index);
    setSelectedMember(tmpList[index]);
	}

	function onChangeInput(e) {
    if (disabled) return;
		setValue(e.target.value);

    const indexList = [];
    const tmp = [];

    for (let i = 0; i < data.length; i++) {
      if (data[i].name.indexOf(e.target.value) > -1) {
        indexList.push(i);
      }
      else if (data[i].phone.indexOf(e.target.value) > -1){
        indexList.push(i);
      }
    }

    if (indexList.length !== 0) {
      for (let i = 0; i < indexList.length; i++) {
        tmp.push(data[indexList[i]]);
      }
      setDataList(tmp);
    }
    else {
      setDataList(data);
    }
	}

	function onFocusInput(e) {
		setChangedPlaceholderColor(false);
		if (!changed) {
			setValue("");
		}
	}

	function onBlurInput(e) {
		setClickCount(clickCount + 1);
		inputRef.current.style.borderColor = theme.line;
	}

  function decidePhoneView(phone, countryCode) {
    return formatPhoneNumber(phone, countryCode, languageMode) || t('main.locker.noPhone');
  }

  function onClickMembershipInfo() {
    if (!selectedMember?.memberShipStartDate || !onClickMembership) return;
    onClickMembership(selectedMember.memberShipStartDate, selectedMember.memberShipEndDate);
  }

  return (
    <TopLevelWrapper>
      <SearchWrapper>
        <SearchInputBox>
          <SearchIcon />
          <Input
            ref={inputRef}
            clickCount={clickCount}
            type={type}
            placeholder={t('main.locker.searchPlaceholder')}
            value={'' || value}
            changedColor={changedPlaceholderColor}
            $disabled={disabled}
            className={`${className} ${changed ? "changed" : ""}`}
            readOnly={disabled}
            onChange={onChangeInput}
            onFocus={onFocusInput}
            onBlur={onBlurInput} />
        </SearchInputBox>
        <InfoWrapper ref={InfoWrapperRef}>
          {
            isLoading &&
            <LoadingOverlay>
              <LottieWrapper>
                <Lottie animationData={LoadingAnimation} loop />
              </LottieWrapper>
            </LoadingOverlay>
          }
          {
            !isLoading &&
            dataList.map((data, index) => (
              <InfoBox
              key={'LockerMemberSearch-' + index}
              data-member-id={data.memberId}
              onClick={() => onClickItem(index)}
              isOnClick={disabled ? data.memberId === selectedMember?.memberId : data.isChecked}
              $isDisabledItem={disabled && data.memberId !== selectedMember?.memberId}
            >
                <Table>
                  <Body>
                    <Tr>
                      <Td>
                        <ImageWrapper>
                          {
                            data.profile ?
                              <ProfileImage src={data.profile} alt={data.name} />
                              :
                              <DefaultLogo />
                          }
                        </ImageWrapper>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <TextWrapper style={{ width: `85px` }}>
                          <Name isChecked={data.isChecked}>{data.name}</Name>
                        </TextWrapper>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td>
                        <TextWrapper style={{ width: `114px` }}>
                          <Phone isChecked={data.isChecked} style={{ width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{decidePhoneView(data.phone, data.countryCode)}</Phone>
                        </TextWrapper>
                      </Td>
                    </Tr>
                  </Body>
                </Table>
                <CheckButton opacity={(disabled ? data.memberId === selectedMember?.memberId : data.isChecked) ? 1 : 0} >
                  <CheckImg/>
                </CheckButton>
              </InfoBox>
            ))
          }
        </InfoWrapper>
        <DivisionLine isChecked={!!selectedMember} />
        <MembershipWrapper isChecked={!!selectedMember}>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{selectedMember && `${selectedMember.name} - ${t('main.locker.extend.activeMembershipInfo')}`}</Text>
          <MemebershipInfoWrapper
            onMouseEnter={() => setIsHoverMembership(true)}
            onMouseLeave={() => setIsHoverMembership(false)}
            onClick={onClickMembershipInfo}
            $isHover={isHoverMembership}
            $clickable={!!selectedMember?.memberShipStartDate && !!onClickMembership}
          >
            <MemebershipInfoUnitWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>{t('main.locker.memberShipLabel')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={isHoverMembership ? theme.primary : theme.text} style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '100%' }}>{selectedMember?.memberShipName}</Text>
            </MemebershipInfoUnitWrapper>
            <MemebershipInfoUnitWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>{t('main.locker.periodLabel')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={isHoverMembership ? theme.primary : theme.text}>
                {selectedMember?.memberShipStartDate && `${moment(selectedMember.memberShipStartDate).format(t('calendarInput.shortDateFormat'))} ~ ${moment(selectedMember.memberShipEndDate).format(t('calendarInput.shortDateFormat'))}`}
              </Text>
            </MemebershipInfoUnitWrapper>
          </MemebershipInfoWrapper>
        </MembershipWrapper>
      </SearchWrapper>
    </TopLevelWrapper>
  )
})

export default LockerMemberSearchInput;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-start;
`;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 345px;
`;

const SearchInputBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  width: 345px;
  height: 40px;
`;

const SearchIcon = styled(SearchingSvg)`
  position: absolute;
  left: 10px;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  pointer-events: none;
  z-index: 1;

  & path {
    fill: ${props => props.theme.subText};
  }
`;

const Input = styled.input`
	font-family: NotoSansKR-Regular;
	font-size: 12px;
	line-height: 20px;

  padding: 0 0 0 40px;

  box-sizing: border-box;
  width: 345px;
  height: 40px;

	outline: none;
  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;
	color: ${props => props.theme.text};
  background-color: ${props => props.$disabled ? props.theme.buttonFill : props.theme.subBackground};
  cursor: ${props => props.$disabled ? 'default' : 'text'};

	::placeholder {
		color: ${props => props.changedColor ? `#FF3131` : props.theme.subText};
	}

  transition: all 0.3s ease-in-out;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 10;
`;

const LottieWrapper = styled.div`
  width: 100px;
  height: 100px;
`;

const InfoWrapper = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 0 7px 10px 0;
  margin-top: 10px;

  box-sizing: border-box;
  width: 345px;
  height: 297px;

  overflow-y: scroll;

  background-color: ${props => props.theme.subBackground};

  gap: 5px;

  &::-webkit-scrollbar {
		width: 7px;
	}

	&::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.scrollBar};
		border: 3px solid transparent;
		height: 25px;
		border-radius: 200px;
	}

	&::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px transparent;
		border: 4px solid transparent;
		background-color: transparent;
		margin-top: 10px;
	}

	&::-webkit-scrollbar-track-piece {
		background-color: transparent;
	}
`;

const DivisionLine = styled.div`
  width: 345px;

  margin: 15px 0;

  border-top: 1px solid ${props => props.theme.line};

  opacity: ${props => props.isChecked ? 1 : 0};
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 0 10px 0 0;

  box-sizing: border-box;
  width: 100%;
  height: 42px;

  border-radius: 7px;

  background-color: ${props => props.isOnClick ? props.theme.primaryAlpha5 : props.theme.subBackground};
  opacity: ${props => props.$isDisabledItem ? 0.3 : 1};
  pointer-events: ${props => props.$isDisabledItem ? 'none' : 'auto'};

  transition: all 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const Table = styled.table`
  display: flex;
  flex-direction: row;

  height: 42px;
`;

const Body = styled.tbody`
  display: flex;
  flex-direction: row;

  gap: 10px;
`;

const Tr = styled.tr`
  display: flex;
  flex-direction: row;
`;

const Td = styled.td`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  overflow: hidden;

  width: 42px;
  height: 42px;

  border: 3px;
`;

const DefaultLogo = styled(DefaultLogoSvg)`
  width: 32px;
  height: 32px;

  & path:nth-child(1) {
    fill: ${props => props.theme.logo1};
  }

  & path:nth-child(2) {
    fill: ${props => props.theme.logo2};
  }

  & path:nth-child(3) {
    fill: ${props => props.theme.logo1};
  }

  & path:nth-child(4) {
    fill: ${props => props.theme.logo3};
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;

  border-radius: 3px;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  overflow: hidden;

  max-height: 20px;
`;

const Name = styled.span`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  transition: all 0.2s ease-in-out;
`;

const Phone = styled.span`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  transition: all 0.2s ease-in-out;
`;

const CheckButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 22px;
  height: 22px;

  border-radius: 22px;
  background-color: ${props => props.theme.primary};

  opacity: ${props => props.opacity};

  transition: all 0.2s ease-in-out;
`;

const CheckImg = styled(Check2Svg)`
  flex-shrink: 0;
  width: 12px;
  height: 10px;

  & path {
    fill: ${props => props.theme.subBackground};
  }

  transition: all 0.2s ease-in-out;
`;

const MembershipWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 72px;

  gap: 10px;
  
  opacity: ${props => props.isChecked ? 1 : 0};

  transition: all 0.2s ease-in-out;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.hover};
  }

  transition: all 0.3s ease-in-out;
`;

const MemebershipInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 20px;

  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  transition: all 0.2s ease-in-out;
`

const MemebershipInfoUnitWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 149px;

  gap: 5px;
`